import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth.guard';
import { guestGuard } from './core/guards/guest.guard';
import { adminGuard } from './core/guards/admin.guard';
import { createUserGuard } from './core/guards/create-user.guard';

export const routes: Routes = [
  {
    path: '',
    redirectTo: 'login',
    pathMatch: 'full',
  },
  {
    // Persistent auth shell — keeps the splash panel mounted across
    // /login, /auth/forgot-password and /auth/recover so the image
    // doesn't re-render when the user navigates between them.
    path: '',
    canActivate: [guestGuard],
    loadComponent: () =>
      import('./features/auth/layouts/auth-layout/auth-layout.component').then(
        (m) => m.AuthLayoutComponent,
      ),
    children: [
      {
        path: 'login',
        loadComponent: () =>
          import('./features/auth/pages/login/login.page').then((m) => m.LoginPage),
      },
      {
        path: 'auth/forgot-password',
        loadComponent: () =>
          import('./features/auth/pages/forgot-password/forgot-password.page').then(
            (m) => m.ForgotPasswordPage,
          ),
      },
      {
        path: 'auth/recover',
        loadComponent: () =>
          import('./features/auth/pages/recover-account/recover-account.page').then(
            (m) => m.RecoverAccountPage,
          ),
      },
      {
        path: 'auth/complete-invite',
        loadComponent: () =>
          import('./features/auth/pages/complete-invite/complete-invite.page').then(
            (m) => m.CompleteInvitePage,
          ),
      },
    ],
  },
  {
    path: 'home',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/home/pages/select-company/select-company.page').then(
        (m) => m.SelectCompanyPage,
      ),
  },
  {
    path: 'companies/:id/surveys',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/home/pages/surveys/surveys.page').then(
        (m) => m.SurveysPage,
      ),
  },
  {
    path: 'surveys/:id',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/home/pages/survey-detail/survey-detail.page').then(
        (m) => m.SurveyDetailPage,
      ),
  },
  {
    // Admin: edit an existing submission's answers in place (reuses SurveyDetailPage).
    path: 'surveys/:id/update-answers',
    canActivate: [authGuard, adminGuard],
    data: { mode: 'update-answers' },
    loadComponent: () =>
      import('./features/home/pages/survey-detail/survey-detail.page').then(
        (m) => m.SurveyDetailPage,
      ),
  },
  {
    // Admin: inspect a user's draft (reuses SurveyDetailPage, no draft persistence).
    path: 'surveys/:id/debug-draft',
    canActivate: [authGuard, adminGuard],
    data: { mode: 'debug-draft' },
    loadComponent: () =>
      import('./features/home/pages/survey-detail/survey-detail.page').then(
        (m) => m.SurveyDetailPage,
      ),
  },
  {
    path: 'admin/debug-drafts',
    canActivate: [authGuard, adminGuard],
    loadComponent: () =>
      import('./features/home/pages/debug-drafts/debug-drafts.page').then(
        (m) => m.DebugDraftsPage,
      ),
  },
  {
    path: 'admin/update-answers',
    canActivate: [authGuard, adminGuard],
    loadComponent: () =>
      import('./features/home/pages/update-answers/update-answers.page').then(
        (m) => m.UpdateAnswersPage,
      ),
  },
  {
    path: 'notifications',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/notifications/pages/notifications/notifications.page').then(
        (m) => m.NotificationsPage,
      ),
  },
  {
    path: 'profile',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/profile/pages/profile/profile.page').then(
        (m) => m.ProfilePage,
      ),
  },
  {
    path: 'profile/change-password',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/profile/pages/change-password/change-password.page').then(
        (m) => m.ChangePasswordPage,
      ),
  },
  {
    path: 'support',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/support/pages/support/support.page').then(
        (m) => m.SupportPage,
      ),
  },
  {
    path: 'create-user',
    canActivate: [authGuard, createUserGuard],
    loadComponent: () =>
      import('./features/create-user/pages/create-user/create-user.page').then(
        (m) => m.CreateUserPage,
      ),
  },
  {
    path: 'settings',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/settings/pages/settings/settings.page').then(
        (m) => m.SettingsPage,
      ),
  },
  {
    path: 'speed-test',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/speed-test/pages/speed-test/speed-test.page').then(
        (m) => m.SpeedTestPage,
      ),
  },
  {
    path: 'reports',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/reports/pages/reports/reports.page').then(
        (m) => m.ReportsPage,
      ),
  },
  {
    path: 'reports/:id',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/reports/pages/report-detail/report-detail.page').then(
        (m) => m.ReportDetailPage,
      ),
  },
  {
    path: 'folder/:id',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./folder/folder.page').then((m) => m.FolderPage),
  },
  {
    path: '**',
    redirectTo: 'login',
  },
];
